import React, { useState, useEffect, useRef, useMemo } from "react";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { Text, Button, Icon } from "@ui-component-library";
import WelcomeStep from "./steps/WelcomeStep";
import IdentificationStep from "./steps/IdentificationStep";
import RegionStep from "./steps/RegionStep";
import BookingStep from "./steps/BookingStep";
import TermsStep from "./steps/TermsStep";
import ThankYouStep from "./steps/ThankYouStep";
import { sendEvent } from "../../api";
import { bookDemo } from "../../api/services/demo";

const BookDemoForm = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoggedIn, setIsLoggedIn] = useState(Window.user_data.isLogged);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [direction, setDirection] = useState("next");
    const [formStatus, setFormStatus] = useState("idle");
    const [formData, setFormData] = useState({})
    const [errors, setErrors] = useState(false);
    const containerRef = useRef(null);
    const stepRef = useRef(null);
    const isWelcomeStep = currentStep === 0;

    useEffect(()=>{
        sendEvent("opened_treeform",{source:"treeform"})
    },[])

    useEffect(() => {
        setIsLoggedIn(Window.user_data.isLogged);
    }, [Window.user_data.isLogged]);

    const submitForm = async () => {
        try {
            const resp = await bookDemo(formData)

            if (resp.status === 200) {
                setErrors(false)
                return true
            } else {
                setErrors(true)
                return false
            }
        } catch (e) {
            setErrors(true)
            console.error("error submitting form", e)
            return false
        }
    }

     const nextStep = async ({ data }) => {
        if (typeof data == "undefined"){
            return setErrors(true)
         }
       
        sendEvent(data?.step, {source:"treeform"})
        const { step, ...formDataPayload } = data || {};

        setFormData((prev) => ({ ...prev, ...formDataPayload }))
        
        setDirection("next");
        const nextStepNumber = Math.min(currentStep + 1, totalSteps);
        if (nextStepNumber === totalSteps-1) {
            setFormStatus("submitting");
            const result=await submitForm();
            
            setCurrentStep(nextStepNumber);
        } else {
            setCurrentStep(nextStepNumber);
        }
    };

    const prevStep = async() => {
        const prevStepNumber = Math.max(currentStep - 1, 0);

        setCurrentStep(prevStepNumber);
        setDirection("prev");
    };



   const handleStepTransition = async (newDirection) => {
    if (isTransitioning) return;

    if (newDirection === "next") {
        const validationResult = stepRef.current?.validateAndReturn?.();

        if (validationResult?.errors) {
            return;
        }

        if (!validationResult?.data) {
            return;
        }

        await nextStep({ data: validationResult.data });
        return;
    }
    await prevStep();
};

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowUp") {
                e.preventDefault();
                handleStepTransition("prev");
            } else if (e.key === "ArrowDown" || e.key === "Enter") { 
                e.preventDefault();
                handleStepTransition("next");
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [currentStep, formData, isTransitioning]);

    useEffect(() => {
        const handleScroll = (e) => {
            e.preventDefault();
            if (e.deltaY > 0) {
                handleStepTransition("next");
            } else if (e.deltaY < 0) {
                handleStepTransition("prev");
            }
        };

        if (containerRef.current) {
            containerRef.current.addEventListener("wheel", handleScroll, {
                passive: false,
            });
        }

        return () => {
            if (containerRef?.current) {
                containerRef.current.removeEventListener("wheel", handleScroll);
            }
        };
    }, [currentStep, isTransitioning]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowUp") {
                e.preventDefault();
                handleStepTransition("prev");
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                handleStepTransition("next");
            } else if (e.key === "Enter") {
                e.preventDefault();
                handleStepTransition("next");
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [currentStep, formData, isTransitioning]);

    
 
    const getProgressValue = () => {
        if (currentStep === 0) return 0;
        if (currentStep === totalSteps) return 100;

        return (currentStep / totalSteps) * 100;
    };

   
    // Render functions

    const StepIndicator = () => {
        const isFirstOrLastStep = currentStep === 0 || currentStep === totalSteps;
        if (isFirstOrLastStep) return null;


        return (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[1001] text-center">
                <span className="text-color"><Text variant="text-sm/leading-normal/normal" className="text-white">Steg {currentStep}/{totalSteps}</Text></span>
            </div>
        );
    };

    const ProgressBar = ({ progress }) => (
        <div className="fixed top-12 left-0 w-full h-1 bg-white/20 z-[1000]">
            <div className={`h-full bg-[#DFFF5D] transition-all duration-500 ease-in-out origin-left`} style={{ width: `${progress}%` }} />
        </div>
    );

    const confirmLoggedInUserData = () => {
        if (isLoggedIn && Window.user_data) {
            const { name, mobilePhoneNumber } = Window.user_data;
            const hasFullName = name && name.includes(" ");
            const hasPhone = !!mobilePhoneNumber;
            return hasFullName && hasPhone;
        }
        return false;
    };

    const stepList = useMemo(() => {
        const list = [];
        list.push({ component: WelcomeStep, props: {} });

        if (!confirmLoggedInUserData()) {
            list.push({ component: IdentificationStep, props: { isLoggedIn, formData } });
        }

        list.push({ component: RegionStep, props: { formData } });

        list.push({ component: BookingStep, props: { formData } });

        list.push({ component: TermsStep, props: {} });
        list.push({component: ThankYouStep, props:{ errors}});

        return list;
    }, [isLoggedIn,formData, errors]);

    const totalSteps = stepList.length


    const renderStep = () => {
        const step = stepList[currentStep];
        if (!step) {
            console.error("Invalid step index", currentStep);
            return null;
        }

        const StepComponent = step.component;
        const stepProps = step.props;

        return (
            <div className="h-screen">
                <StepComponent
                    ref = {stepRef}
                    key={currentStep}
                    onNext={nextStep}
                    {...stepProps}
                />
            </div>
        );
    };

    
    const NavigationButtons = () => {
    
        const showButtons = currentStep > 0 && currentStep < 4;

        if (!showButtons) return null;
        return (
            <div className="fixed bottom-[75px] right-5 flex flex-row gap-2.5 z-10 sm:bottom-5">

                <Button
                    tw
                    onClick={() => handleStepTransition("prev")}
                    size="icon"
                    variant="tertiary"
                    className="-rotate-90 bg-spring-yellow-500"
                >
                    <Icon
                        name="ArrowRightSimple"
                        size="large"
                    />
                </Button>
                <Button
                    tw
                    onClick={() => handleStepTransition("next")}
                    size="icon"
                    variant="tertiary"
                    className="rotate-90 bg-spring-yellow-500"
                >
                    <Icon
                        name="ArrowRightSimple"
                        size="large"
                    />
                </Button>
            </div>)
    }

    return (
        <>
        <TailwindWrapper>
            <div className="bg-[#023436]">
                {isWelcomeStep && <div className="p-6">
                    {/* Current fix for now to change the logo color */}
                    <div
                        className="w-[150px] h-[40px] bg-spring-yellow-500"
                        style={{
                            maskImage: `url(${Window.static_path}img/icons/treebula_lockup_charcoal.svg)`,
                            maskRepeat: 'no-repeat',
                            maskSize: 'contain',
                            WebkitMaskImage: `url(${Window.static_path}img/icons/treebula_lockup_charcoal.svg)`,
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskSize: 'contain'
                        }}
                    />
                </div>}
                <div className="container mx-auto">

                    <div className="bg-[#023436]" ref={containerRef}>
                        {!isWelcomeStep && (
                            <>
                                <StepIndicator />
                                <ProgressBar progress={getProgressValue()} />
                            </>
                        )}
                        {renderStep()}
                        <NavigationButtons />
                    </div>
                </div>
            </div>
        </TailwindWrapper>
        </>
    )
}
export default BookDemoForm;